import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { vehicleAPI, driverAPI } from '../lib/api';
import { PendingLeasedVehicles } from '../components/leasing/PendingLeasedVehicles';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
    Plus, Search, Truck, Edit, Trash2, Loader2, 
    MoreVertical, Filter, History, Camera, Calendar, User, Bell,
    Fuel, RotateCcw, Image, X, Sparkles, Car, Package, Wrench
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import AiMaintenanceModal from '../components/vehicles/AiMaintenanceModal';
import VehicleSummaryModal from '../components/vehicles/VehicleSummaryModal';

const initialFormData = {
    license_plate: '',
    vin: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vehicle_type: 'truck',
    status: 'active',
    fuel_type: 'diesel',
    current_mileage: 0,
    assigned_driver_id: '',
    assistance_phone: '',
    registration_date: '',
    tyre_number: '',
    insurance_expiry: '',
    ownership: 'owned',
    leasing_reminders: [],
    billing_category: 'carrier'  // Default for Hybrid edition
};

const vehicleTypes = ['truck', 'van', 'sedan', 'suv', 'bus', 'trailer'];
const fuelTypes = ['diesel', 'gasoline', 'electric', 'hybrid', 'cng'];
const statusOptions = ['active', 'maintenance', 'inactive', 'retired'];
const ownershipOptions = ['owned', 'leasing'];

// Predefined reminder types for leasing vehicles
const reminderTypes = [
    { id: 'tyre_change', labelEn: 'Tyre Change', labelEl: 'Αλλαγή Ελαστικών', defaultKm: 40000 },
    { id: 'oil_change', labelEn: 'Oil Change', labelEl: 'Αλλαγή Λαδιών', defaultKm: 15000 },
    { id: 'brake_service', labelEn: 'Brake Service', labelEl: 'Σέρβις Φρένων', defaultKm: 30000 },
    { id: 'filter_replacement', labelEn: 'Filter Replacement', labelEl: 'Αλλαγή Φίλτρων', defaultKm: 20000 },
    { id: 'battery_check', labelEn: 'Battery Check', labelEl: 'Έλεγχος Μπαταρίας', defaultKm: 50000 },
    { id: 'lease_return', labelEn: 'Lease Return Date', labelEl: 'Ημερ. Επιστροφής Leasing', defaultKm: null },
];

const StatusBadge = ({ status }) => {
    const styles = {
        active: 'badge-success',
        maintenance: 'badge-warning',
        inactive: 'badge-default',
        retired: 'badge-danger'
    };
    return (
        <span className={`badge ${styles[status] || 'badge-default'}`}>
            {status}
        </span>
    );
};

export default function VehiclesPage() {
    const { canManage, isAdmin } = useAuth();
    const { t, language } = useLanguage();
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [submitting, setSubmitting] = useState(false);
    const [companyEdition, setCompanyEdition] = useState('carrier');
    
    // History modal state
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedVehicleHistory, setSelectedVehicleHistory] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Replacement car return modal state
    const [returnModalOpen, setReturnModalOpen] = useState(false);
    const [selectedReplacementVehicle, setSelectedReplacementVehicle] = useState(null);
    const [replacementCarData, setReplacementCarData] = useState(null);
    const [returnLoading, setReturnLoading] = useState(false);

    // AI Maintenance suggestions modal state
    const [aiMaintenanceModalOpen, setAiMaintenanceModalOpen] = useState(false);
    const [newlyCreatedVehicle, setNewlyCreatedVehicle] = useState(null);

    // Replacement car modal state (when changing status to maintenance)
    const [showReplacementPrompt, setShowReplacementPrompt] = useState(false);
    const [pendingMaintenanceVehicle, setPendingMaintenanceVehicle] = useState(null);
    const [needsReplacementCar, setNeedsReplacementCar] = useState(false);
    const [replacementCarFormData, setReplacementCarFormData] = useState({
        make: '',
        model: '',
        license_plate: '',
        fuel_level: 'half',
        odometer_reading: '',
        notes: ''
    });
    const [submittingReplacement, setSubmittingReplacement] = useState(false);

    // Vehicle Summary modal state
    const [summaryModalOpen, setSummaryModalOpen] = useState(false);
    const [selectedVehicleSummary, setSelectedVehicleSummary] = useState(null);

    const fuelLevelLabels = {
        empty: { label: 'Empty', icon: '○○○○', color: 'text-red-500' },
        quarter: { label: '1/4', icon: '●○○○', color: 'text-orange-500' },
        half: { label: '1/2', icon: '●●○○', color: 'text-yellow-500' },
        three_quarter: { label: '3/4', icon: '●●●○', color: 'text-green-400' },
        full: { label: 'Full', icon: '●●●●', color: 'text-green-600' },
    };

    const fuelLevelOptions = [
        { value: 'empty', label: 'Empty', labelEl: 'Άδειο', icon: '○○○○', color: 'text-red-500' },
        { value: 'quarter', label: '1/4', labelEl: '1/4', icon: '●○○○', color: 'text-orange-500' },
        { value: 'half', label: '1/2', labelEl: '1/2', icon: '●●○○', color: 'text-yellow-500' },
        { value: 'three_quarter', label: '3/4', labelEl: '3/4', icon: '●●●○', color: 'text-green-400' },
        { value: 'full', label: 'Full', labelEl: 'Γεμάτο', icon: '●●●●', color: 'text-green-600' },
    ];

    const photoTypeLabels = {
        front: 'Front',
        back: 'Back',
        left: 'Left Side',
        right: 'Right Side',
        interior_front: 'Interior Front',
        interior_back: 'Interior Back'
    };

    // Fetch company edition
    useEffect(() => {
        const fetchCompanyEdition = async () => {
            try {
                const response = await api.get('/auth/company');
                setCompanyEdition(response.data.edition || 'carrier');
            } catch (error) {
                console.error('Error fetching company edition:', error);
            }
        };
        fetchCompanyEdition();
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const [vehiclesRes, driversRes] = await Promise.all([
                vehicleAPI.getAll(),
                driverAPI.getAll().catch(() => ({ data: [] }))
            ]);
            setVehicles(vehiclesRes.data);
            setDrivers(driversRes.data);
        } catch (error) {
            toast.error('Failed to fetch vehicles');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const payload = {
                ...formData,
                year: parseInt(formData.year),
                current_mileage: parseFloat(formData.current_mileage),
                assigned_driver_id: formData.assigned_driver_id === 'none' ? null : formData.assigned_driver_id || null
            };

            if (editingVehicle) {
                await vehicleAPI.update(editingVehicle.id, payload);
                toast.success(language === 'el' ? 'Το όχημα ενημερώθηκε' : 'Vehicle updated successfully');
                setIsDialogOpen(false);
                setEditingVehicle(null);
                setFormData(initialFormData);
            } else {
                const response = await vehicleAPI.create(payload);
                toast.success(language === 'el' ? 'Το όχημα δημιουργήθηκε' : 'Vehicle created successfully');
                setIsDialogOpen(false);
                setFormData(initialFormData);
                
                // Show AI Maintenance suggestions modal for new vehicles
                const createdVehicle = {
                    id: response.data.id,
                    make: payload.make,
                    model: payload.model,
                    year: payload.year,
                    fuel_type: payload.fuel_type,
                    license_plate: payload.license_plate
                };
                setNewlyCreatedVehicle(createdVehicle);
                setAiMaintenanceModalOpen(true);
            }
            
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Operation failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (vehicle) => {
        setEditingVehicle(vehicle);
        setFormData({
            license_plate: vehicle.license_plate,
            vin: vehicle.vin,
            make: vehicle.make,
            model: vehicle.model,
            year: vehicle.year,
            vehicle_type: vehicle.vehicle_type,
            status: vehicle.status,
            fuel_type: vehicle.fuel_type,
            current_mileage: vehicle.current_mileage,
            assigned_driver_id: vehicle.assigned_driver_id || '',
            assistance_phone: vehicle.assistance_phone || '',
            registration_date: vehicle.registration_date || '',
            tyre_number: vehicle.tyre_number || '',
            insurance_expiry: vehicle.insurance_expiry || '',
            ownership: vehicle.ownership || 'owned',
            leasing_reminders: vehicle.leasing_reminders || [],
            billing_category: vehicle.billing_category || 'carrier'
        });
        setIsDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
        
        try {
            await vehicleAPI.delete(id);
            toast.success('Vehicle deleted successfully');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Delete failed');
        }
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setEditingVehicle(null);
        setFormData(initialFormData);
    };

    // Handle status change to maintenance - trigger replacement car prompt
    const handleStatusChangeToMaintenance = (newStatus) => {
        if (newStatus === 'maintenance' && editingVehicle && editingVehicle.status !== 'maintenance') {
            // Vehicle is being changed to maintenance - show replacement prompt
            setFormData({...formData, status: newStatus});
            setShowReplacementPrompt(true);
            setPendingMaintenanceVehicle(editingVehicle);
        } else {
            setFormData({...formData, status: newStatus});
        }
    };

    // Submit replacement car along with vehicle status update
    const handleReplacementCarSubmit = async () => {
        if (needsReplacementCar) {
            if (!replacementCarFormData.make || !replacementCarFormData.license_plate || !replacementCarFormData.odometer_reading) {
                toast.error(language === 'el' ? 'Συμπληρώστε Μάρκα, Πινακίδα και Χιλιόμετρα' : 'Please fill Make, License Plate and Kilometers');
                return;
            }
        }

        setSubmittingReplacement(true);
        try {
            // First update the vehicle status to maintenance
            await vehicleAPI.update(pendingMaintenanceVehicle.id, { status: 'maintenance' });

            // If replacement car is needed, create maintenance record and replacement car
            if (needsReplacementCar) {
                // Create a maintenance record first
                const maintenanceResponse = await api.post('/maintenance', {
                    vehicle_id: pendingMaintenanceVehicle.id,
                    maintenance_type: 'routine',
                    description: language === 'el' 
                        ? `Συντήρηση για ${pendingMaintenanceVehicle.make} ${pendingMaintenanceVehicle.model}` 
                        : `Maintenance for ${pendingMaintenanceVehicle.make} ${pendingMaintenanceVehicle.model}`,
                    status: 'in_progress',
                    scheduled_date: new Date().toISOString().split('T')[0]
                });

                // Then create the replacement car
                await api.post(`/maintenance/${maintenanceResponse.data.id}/replacement-car`, {
                    make: replacementCarFormData.make,
                    model: replacementCarFormData.model || '',
                    license_plate: replacementCarFormData.license_plate,
                    fuel_level: replacementCarFormData.fuel_level,
                    odometer_reading: parseFloat(replacementCarFormData.odometer_reading),
                    notes: replacementCarFormData.notes
                });

                toast.success(language === 'el' 
                    ? 'Η κατάσταση ενημερώθηκε και το όχημα αντικατάστασης καταχωρήθηκε' 
                    : 'Status updated and replacement car registered');
            } else {
                toast.success(language === 'el' ? 'Η κατάσταση ενημερώθηκε σε συντήρηση' : 'Status updated to maintenance');
            }

            // Reset states
            setShowReplacementPrompt(false);
            setPendingMaintenanceVehicle(null);
            setNeedsReplacementCar(false);
            setReplacementCarFormData({
                make: '',
                model: '',
                license_plate: '',
                fuel_level: 'half',
                odometer_reading: '',
                notes: ''
            });
            setIsDialogOpen(false);
            setEditingVehicle(null);
            setFormData(initialFormData);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || (language === 'el' ? 'Αποτυχία ενημέρωσης' : 'Update failed'));
        } finally {
            setSubmittingReplacement(false);
        }
    };

    const handleCancelReplacementPrompt = () => {
        setShowReplacementPrompt(false);
        setPendingMaintenanceVehicle(null);
        setNeedsReplacementCar(false);
        setReplacementCarFormData({
            make: '',
            model: '',
            license_plate: '',
            fuel_level: 'half',
            odometer_reading: '',
            notes: ''
        });
        // Revert status change
        setFormData({...formData, status: editingVehicle?.status || 'active'});
    };

    const handleViewReturnInfo = async (vehicle) => {
        if (!vehicle.replacement_car_id) {
            toast.error('No replacement car data found');
            return;
        }
        
        setSelectedReplacementVehicle(vehicle);
        setReturnLoading(true);
        setReturnModalOpen(true);
        
        try {
            const response = await api.get(`/replacement-cars/${vehicle.replacement_car_id}`);
            setReplacementCarData(response.data);
        } catch (error) {
            console.error('Failed to fetch replacement car data:', error);
            toast.error('Failed to load replacement car info');
        } finally {
            setReturnLoading(false);
        }
    };

    const handleReturnVehicle = async () => {
        if (!selectedReplacementVehicle) return;
        
        try {
            // Mark vehicle as inactive/returned
            await vehicleAPI.update(selectedReplacementVehicle.id, { status: 'inactive' });
            toast.success('Replacement vehicle marked as returned');
            setReturnModalOpen(false);
            setSelectedReplacementVehicle(null);
            setReplacementCarData(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to process return');
        }
    };

    const filteredVehicles = vehicles.filter(vehicle => {
        const matchesSearch = 
            vehicle.license_plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vehicle.vin.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || 
            vehicle.status === filterStatus || 
            (filterStatus === 'replacement' && vehicle.is_replacement);
        return matchesSearch && matchesStatus;
    });

    const getDriverName = (driverId) => {
        const driver = drivers.find(d => d.id === driverId);
        return driver?.name || '-';
    };

    const handleViewHistory = async (vehicle) => {
        setSelectedVehicleHistory(vehicle);
        setHistoryModalOpen(true);
        setHistoryLoading(true);
        try {
            const response = await api.get(`/vehicles/${vehicle.id}/history?days=30`);
            setHistoryData(response.data);
        } catch (error) {
            toast.error('Failed to fetch vehicle history');
        } finally {
            setHistoryLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="vehicles-page">
            {canManage() && <PendingLeasedVehicles onConfirmed={fetchData} />}
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{t('vehicles.title')}</h1>
                    <p className="text-zinc-500">{vehicles.length} {t('vehicles.totalEntries')}</p>
                </div>
                
                {canManage() && (
                    <Dialog open={isDialogOpen} onOpenChange={(open) => open ? setIsDialogOpen(true) : handleDialogClose()}>
                        <DialogTrigger asChild>
                            <Button 
                                className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm"
                                data-testid="add-vehicle-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {t('vehicles.addVehicle')}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-white">
                                    {editingVehicle ? t('vehicles.editVehicle') : t('vehicles.addVehicle')}
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{t('vehicles.licensePlate')}</Label>
                                        <Input
                                            value={formData.license_plate}
                                            onChange={(e) => setFormData({...formData, license_plate: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="ABC-1234"
                                            required
                                            data-testid="vehicle-license-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{t('vehicles.vin')}</Label>
                                        <Input
                                            value={formData.vin}
                                            onChange={(e) => setFormData({...formData, vin: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="1HGBH41JXMN109186"
                                            required
                                            data-testid="vehicle-vin-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{t('vehicles.make')}</Label>
                                        <Input
                                            value={formData.make}
                                            onChange={(e) => setFormData({...formData, make: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="Ford"
                                            required
                                            data-testid="vehicle-make-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{t('vehicles.model')}</Label>
                                        <Input
                                            value={formData.model}
                                            onChange={(e) => setFormData({...formData, model: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="F-150"
                                            required
                                            data-testid="vehicle-model-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{t('vehicles.year')}</Label>
                                        <Input
                                            type="number"
                                            value={formData.year}
                                            onChange={(e) => setFormData({...formData, year: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            min="1990"
                                            max={new Date().getFullYear() + 1}
                                            required
                                            data-testid="vehicle-year-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">Vehicle Type</Label>
                                        <Select 
                                            value={formData.vehicle_type} 
                                            onValueChange={(value) => setFormData({...formData, vehicle_type: value})}
                                        >
                                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="vehicle-type-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                {vehicleTypes.map(type => (
                                                    <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    {/* Billing Category - Only for Hybrid Edition */}
                                    {companyEdition === 'hybrid' && (
                                        <div className="form-group col-span-2">
                                            <Label className="text-zinc-300 text-sm mb-1 block">
                                                {language === 'el' ? 'Κατηγορία Χρέωσης' : 'Billing Category'}
                                            </Label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData({...formData, billing_category: 'inspection'})}
                                                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                                                        formData.billing_category === 'inspection'
                                                            ? 'border-[#1B7EB5] bg-[#1B7EB5]/10'
                                                            : 'border-slate-200 bg-white hover:border-slate-300'
                                                    }`}
                                                    data-testid="billing-category-inspection"
                                                >
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Car className={`w-5 h-5 ${formData.billing_category === 'inspection' ? 'text-[#1B7EB5]' : 'text-slate-400'}`} />
                                                        <span className={`font-semibold ${formData.billing_category === 'inspection' ? 'text-[#1B7EB5]' : 'text-slate-700'}`}>
                                                            Inspection
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-slate-500">
                                                        {language === 'el' ? 'Επιβατικά, ταξί, πωλητές' : 'Passenger, taxi, sales'}
                                                    </p>
                                                    <p className="text-xs text-[#1B7EB5] mt-1 font-medium">
                                                        €6-4/{language === 'el' ? 'όχημα' : 'vehicle'}
                                                    </p>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData({...formData, billing_category: 'carrier'})}
                                                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                                                        formData.billing_category === 'carrier'
                                                            ? 'border-[#8CC774] bg-[#8CC774]/10'
                                                            : 'border-slate-200 bg-white hover:border-slate-300'
                                                    }`}
                                                    data-testid="billing-category-carrier"
                                                >
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Package className={`w-5 h-5 ${formData.billing_category === 'carrier' ? 'text-[#8CC774]' : 'text-slate-400'}`} />
                                                        <span className={`font-semibold ${formData.billing_category === 'carrier' ? 'text-[#8CC774]' : 'text-slate-700'}`}>
                                                            Carrier
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-slate-500">
                                                        {language === 'el' ? 'Φορτηγά, παραδόσεις' : 'Trucks, deliveries'}
                                                    </p>
                                                    <p className="text-xs text-[#8CC774] mt-1 font-medium">
                                                        €9-5/{language === 'el' ? 'όχημα' : 'vehicle'}
                                                    </p>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">Fuel Type</Label>
                                        <Select 
                                            value={formData.fuel_type} 
                                            onValueChange={(value) => setFormData({...formData, fuel_type: value})}
                                        >
                                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="vehicle-fuel-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                {fuelTypes.map(type => (
                                                    <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">Status</Label>
                                        <Select 
                                            value={formData.status} 
                                            onValueChange={(value) => handleStatusChangeToMaintenance(value)}
                                        >
                                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="vehicle-status-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                {statusOptions.map(status => (
                                                    <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">Current Mileage (km)</Label>
                                        <Input
                                            type="number"
                                            value={formData.current_mileage}
                                            onChange={(e) => setFormData({...formData, current_mileage: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            min="0"
                                            data-testid="vehicle-mileage-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">Assigned Driver</Label>
                                        <Select 
                                            value={formData.assigned_driver_id} 
                                            onValueChange={(value) => setFormData({...formData, assigned_driver_id: value})}
                                        >
                                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="vehicle-driver-select">
                                                <SelectValue placeholder="Select driver" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                <SelectItem value="none">Unassigned</SelectItem>
                                                {drivers.map(driver => (
                                                    <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="form-group col-span-2">
                                        <Label className="text-zinc-300 text-sm mb-1 block">Roadside Assistance Phone</Label>
                                        <Input
                                            type="tel"
                                            value={formData.assistance_phone}
                                            onChange={(e) => setFormData({...formData, assistance_phone: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="+1 800 555 1234"
                                            data-testid="vehicle-assistance-input"
                                        />
                                        <p className="text-xs text-zinc-400 mt-1">Emergency contact number for roadside assistance</p>
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{t('vehicles.registrationDate')}</Label>
                                        <Input
                                            type="date"
                                            value={formData.registration_date}
                                            onChange={(e) => setFormData({...formData, registration_date: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            data-testid="vehicle-registration-date-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{t('vehicles.tyreNumber')}</Label>
                                        <Input
                                            value={formData.tyre_number}
                                            onChange={(e) => setFormData({...formData, tyre_number: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="225/45R17"
                                            data-testid="vehicle-tyre-number-input"
                                        />
                                    </div>
                                    <div className="form-group col-span-2">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{t('vehicles.insuranceExpiry')}</Label>
                                        <Input
                                            type="date"
                                            value={formData.insurance_expiry}
                                            onChange={(e) => setFormData({...formData, insurance_expiry: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            data-testid="vehicle-insurance-expiry-input"
                                        />
                                        <p className="text-xs text-zinc-400 mt-1">{t('vehicles.insuranceExpiryHelp')}</p>
                                    </div>
                                    
                                    {/* Ownership Field */}
                                    <div className="form-group col-span-2">
                                        <Label className="text-zinc-300 text-sm mb-1 block">{language === 'el' ? 'Ιδιοκτησία' : 'Ownership'}</Label>
                                        <Select 
                                            value={formData.ownership} 
                                            onValueChange={(value) => setFormData({...formData, ownership: value, leasing_reminders: value === 'owned' ? [] : formData.leasing_reminders})}
                                        >
                                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="vehicle-ownership-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border-slate-200">
                                                <SelectItem value="owned">{language === 'el' ? 'Ιδιόκτητο' : 'Owned'}</SelectItem>
                                                <SelectItem value="leasing">{language === 'el' ? 'Leasing' : 'Leasing'}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    {/* Leasing Reminders Section - Only shown when ownership is 'leasing' */}
                                    {formData.ownership === 'leasing' && (
                                        <div className="form-group col-span-2 border border-zinc-700 rounded-lg p-4 bg-zinc-800">
                                            <Label className="text-[#1B7EB5] font-semibold mb-3 block">
                                                {language === 'el' ? 'Υπενθυμίσεις Leasing' : 'Leasing Reminders'}
                                            </Label>
                                            <p className="text-xs text-zinc-400 mb-4">
                                                {language === 'el' 
                                                    ? 'Προσθέστε υπενθυμίσεις για συντήρηση βάσει χιλιομέτρων ή ημερομηνίας' 
                                                    : 'Add maintenance reminders based on kilometers or date'}
                                            </p>
                                            
                                            <div className="space-y-3">
                                                {reminderTypes.map((reminderType) => {
                                                    const existingReminder = formData.leasing_reminders?.find(r => r.type === reminderType.id);
                                                    const isEnabled = !!existingReminder;
                                                    
                                                    return (
                                                        <div key={reminderType.id} className="flex items-center gap-3 p-3 bg-zinc-700 rounded-lg border border-zinc-600">
                                                            <input
                                                                type="checkbox"
                                                                id={`reminder-${reminderType.id}`}
                                                                checked={isEnabled}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        // Add reminder
                                                                        setFormData({
                                                                            ...formData,
                                                                            leasing_reminders: [
                                                                                ...(formData.leasing_reminders || []),
                                                                                { 
                                                                                    type: reminderType.id, 
                                                                                    km: reminderType.defaultKm,
                                                                                    date: reminderType.id === 'lease_return' ? '' : null
                                                                                }
                                                                            ]
                                                                        });
                                                                    } else {
                                                                        // Remove reminder
                                                                        setFormData({
                                                                            ...formData,
                                                                            leasing_reminders: formData.leasing_reminders?.filter(r => r.type !== reminderType.id) || []
                                                                        });
                                                                    }
                                                                }}
                                                                className="w-4 h-4 rounded border-slate-300 text-[#1B7EB5] focus:ring-[#1B7EB5]"
                                                            />
                                                            <label htmlFor={`reminder-${reminderType.id}`} className="flex-1 text-sm text-white font-medium">
                                                                {language === 'el' ? reminderType.labelEl : reminderType.labelEn}
                                                            </label>
                                                            
                                                            {isEnabled && (
                                                                <>
                                                                    {reminderType.id === 'lease_return' ? (
                                                                        <Input
                                                                            type="date"
                                                                            value={existingReminder?.date || ''}
                                                                            onChange={(e) => {
                                                                                setFormData({
                                                                                    ...formData,
                                                                                    leasing_reminders: formData.leasing_reminders?.map(r => 
                                                                                        r.type === reminderType.id 
                                                                                            ? { ...r, date: e.target.value }
                                                                                            : r
                                                                                    ) || []
                                                                                });
                                                                            }}
                                                                            className="w-40 bg-white border-slate-300 text-slate-900 text-sm"
                                                                            data-testid={`reminder-date-${reminderType.id}`}
                                                                        />
                                                                    ) : (
                                                                        <div className="flex items-center gap-2">
                                                                            <Input
                                                                                type="number"
                                                                                value={existingReminder?.km || ''}
                                                                                onChange={(e) => {
                                                                                    setFormData({
                                                                                        ...formData,
                                                                                        leasing_reminders: formData.leasing_reminders?.map(r => 
                                                                                            r.type === reminderType.id 
                                                                                                ? { ...r, km: parseInt(e.target.value) || 0 }
                                                                                                : r
                                                                                        ) || []
                                                                                    });
                                                                                }}
                                                                                className="w-28 bg-white border-slate-300 text-slate-900 text-sm"
                                                                                placeholder="km"
                                                                                data-testid={`reminder-km-${reminderType.id}`}
                                                                            />
                                                                            <span className="text-xs text-slate-500">km</span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button 
                                        type="button" 
                                        variant="outline" 
                                        onClick={handleDialogClose}
                                        className="border-zinc-700 text-slate-500 hover:text-slate-700"
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white"
                                        disabled={submitting}
                                        data-testid="vehicle-submit-btn"
                                    >
                                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingVehicle ? 'Update' : 'Create')}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                {/* Stats cards */}
                <div className="flex gap-3 mr-auto">
                    <div className="px-3 py-2 bg-slate-100 rounded-lg">
                        <span className="text-sm text-slate-600">{language === 'el' ? 'Σύνολο' : 'Total'}: </span>
                        <span className="font-semibold text-slate-800">{vehicles.length}</span>
                    </div>
                    <div className="px-3 py-2 bg-green-100 rounded-lg">
                        <span className="text-sm text-green-600">{language === 'el' ? 'Ενεργά' : 'Active'}: </span>
                        <span className="font-semibold text-green-700">{vehicles.filter(v => v.status === 'active' && !v.is_replacement).length}</span>
                    </div>
                    {vehicles.filter(v => v.is_replacement).length > 0 && (
                        <div className="px-3 py-2 bg-orange-100 rounded-lg">
                            <span className="text-sm text-orange-600">{language === 'el' ? 'Αντικατάστασης' : 'Replacement'}: </span>
                            <span className="font-semibold text-orange-700">{vehicles.filter(v => v.is_replacement).length}</span>
                        </div>
                    )}
                </div>
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                        placeholder="Search by license plate, make, model, or VIN..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white border-slate-300 text-slate-900"
                        data-testid="vehicle-search-input"
                    />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-900 w-40" data-testid="vehicle-filter-status">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Status</SelectItem>
                        {statusOptions.map(status => (
                            <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                        ))}
                        <SelectItem value="replacement">{language === 'el' ? 'Αντικατάστασης' : 'Replacement'}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="fleet-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{language === 'el' ? 'Όχημα' : 'Vehicle'}</th>
                                <th>{language === 'el' ? 'Πινακίδα' : 'License Plate'}</th>
                                <th>{language === 'el' ? 'Τύπος' : 'Type'}</th>
                                {companyEdition === 'hybrid' && <th>{language === 'el' ? 'Χρέωση' : 'Billing'}</th>}
                                <th>{language === 'el' ? 'Ιδιοκτησία' : 'Ownership'}</th>
                                <th>{language === 'el' ? 'Κατάσταση' : 'Status'}</th>
                                <th>{language === 'el' ? 'Χιλιόμετρα' : 'Mileage'}</th>
                                <th>{language === 'el' ? 'Λήξη Ασφάλ.' : 'Insurance'}</th>
                                <th>{language === 'el' ? 'Οδηγός' : 'Driver'}</th>
                                {canManage() && <th className="w-12">{language === 'el' ? 'Ενέργειες' : 'Actions'}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVehicles.length === 0 ? (
                                <tr>
                                    <td colSpan={canManage() ? (companyEdition === 'hybrid' ? 10 : 9) : (companyEdition === 'hybrid' ? 9 : 8)} className="text-center py-8">
                                        <div className="empty-state">
                                            <Truck className="empty-state-icon mx-auto" />
                                            <p>{language === 'el' ? 'Δεν βρέθηκαν οχήματα' : 'No vehicles found'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredVehicles.map((vehicle) => {
                                    const isInsuranceExpiringSoon = vehicle.insurance_expiry && 
                                        new Date(vehicle.insurance_expiry) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                                    const isInsuranceExpired = vehicle.insurance_expiry && 
                                        new Date(vehicle.insurance_expiry) < new Date();
                                    const hasActiveReminders = vehicle.leasing_reminders && vehicle.leasing_reminders.length > 0;
                                    
                                    return (
                                    <tr 
                                        key={vehicle.id} 
                                        className={`table-row-hover cursor-pointer ${vehicle.is_replacement ? 'bg-orange-50' : ''}`} 
                                        data-testid={`vehicle-row-${vehicle.id}`}
                                        onClick={() => {
                                            setSelectedVehicleSummary(vehicle);
                                            setSummaryModalOpen(true);
                                        }}
                                    >
                                        <td>
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${vehicle.is_replacement ? 'bg-orange-500' : 'bg-zinc-800'}`}>
                                                    <Truck className={`w-5 h-5 ${vehicle.is_replacement ? 'text-white' : 'text-zinc-400'}`} />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium text-slate-800">{vehicle.make} {vehicle.model}</p>
                                                        {vehicle.is_replacement && (
                                                            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-orange-500 text-white">
                                                                {language === 'el' ? 'Αντικατάσταση' : 'Replacement'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-zinc-500">{vehicle.year}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <code className={`font-mono ${vehicle.is_replacement ? 'text-orange-600' : 'text-[#1B7EB5]'}`}>{vehicle.license_plate}</code>
                                        </td>
                                        <td className="capitalize">{vehicle.is_replacement ? (language === 'el' ? 'Αντικατάσταση' : 'Replacement') : vehicle.vehicle_type}</td>
                                        {companyEdition === 'hybrid' && (
                                            <td>
                                                <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 w-fit ${
                                                    vehicle.billing_category === 'inspection' 
                                                        ? 'bg-[#1B7EB5]/10 text-[#1B7EB5]' 
                                                        : 'bg-[#8CC774]/10 text-[#8CC774]'
                                                }`}>
                                                    {vehicle.billing_category === 'inspection' 
                                                        ? <><Car className="w-3 h-3" /> Inspection</>
                                                        : <><Package className="w-3 h-3" /> Carrier</>}
                                                </span>
                                            </td>
                                        )}
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    vehicle.ownership === 'leasing' 
                                                        ? 'bg-purple-100 text-purple-700' 
                                                        : 'bg-green-100 text-green-700'
                                                }`}>
                                                    {vehicle.ownership === 'leasing' 
                                                        ? 'Leasing' 
                                                        : (language === 'el' ? 'Ιδιόκτητο' : 'Owned')}
                                                </span>
                                                {hasActiveReminders && (
                                                    <Bell className="w-4 h-4 text-purple-500" title={language === 'el' ? 'Ενεργές υπενθυμίσεις' : 'Active reminders'} />
                                                )}
                                            </div>
                                        </td>
                                        <td><StatusBadge status={vehicle.status} /></td>
                                        <td>{vehicle.current_mileage.toLocaleString()} km</td>
                                        <td>
                                            {vehicle.insurance_expiry ? (
                                                <span className={`text-sm ${isInsuranceExpired ? 'text-red-600 font-medium' : isInsuranceExpiringSoon ? 'text-yellow-600' : 'text-slate-600'}`}>
                                                    {new Date(vehicle.insurance_expiry).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US')}
                                                    {isInsuranceExpired && <span className="ml-1 text-xs">({language === 'el' ? 'Έληξε' : 'Expired'})</span>}
                                                </span>
                                            ) : (
                                                <span className="text-zinc-400">-</span>
                                            )}
                                        </td>
                                        <td>{getDriverName(vehicle.assigned_driver_id)}</td>
                                        {canManage() && (
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                        <DropdownMenuItem 
                                                            onClick={() => handleEdit(vehicle)}
                                                            className="cursor-pointer"
                                                            data-testid={`edit-vehicle-${vehicle.id}`}
                                                        >
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            onClick={() => handleViewHistory(vehicle)}
                                                            className="cursor-pointer"
                                                            data-testid={`history-vehicle-${vehicle.id}`}
                                                        >
                                                            <History className="w-4 h-4 mr-2" />
                                                            View History
                                                        </DropdownMenuItem>
                                                        {vehicle.is_replacement && (
                                                            <DropdownMenuItem 
                                                                onClick={() => handleViewReturnInfo(vehicle)}
                                                                className="cursor-pointer text-orange-600 focus:text-orange-600"
                                                                data-testid={`return-vehicle-${vehicle.id}`}
                                                            >
                                                                <RotateCcw className="w-4 h-4 mr-2" />
                                                                {language === 'el' ? 'Επιστροφή' : 'Return'}
                                                            </DropdownMenuItem>
                                                        )}
                                                        {isAdmin() && (
                                                            <DropdownMenuItem 
                                                                onClick={() => handleDelete(vehicle.id)}
                                                                className="cursor-pointer text-red-400 focus:text-red-400"
                                                                data-testid={`delete-vehicle-${vehicle.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Delete
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        )}
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Replacement Car Prompt Modal */}
            <Dialog open={showReplacementPrompt} onOpenChange={(open) => !open && handleCancelReplacementPrompt()}>
                <DialogContent className="bg-white border-slate-200 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <Car className="w-5 h-5 text-orange-500" />
                            {language === 'el' ? 'Αλλαγή σε Συντήρηση' : 'Change to Maintenance'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                            <p className="text-orange-800 text-sm">
                                {language === 'el' 
                                    ? `Το όχημα "${pendingMaintenanceVehicle?.make} ${pendingMaintenanceVehicle?.model}" (${pendingMaintenanceVehicle?.license_plate}) θα αλλάξει σε κατάσταση συντήρησης.`
                                    : `Vehicle "${pendingMaintenanceVehicle?.make} ${pendingMaintenanceVehicle?.model}" (${pendingMaintenanceVehicle?.license_plate}) will be set to maintenance.`
                                }
                            </p>
                        </div>

                        {/* Replacement car checkbox */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={needsReplacementCar}
                                    onChange={(e) => setNeedsReplacementCar(e.target.checked)}
                                    className="w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                                    data-testid="replacement-car-checkbox"
                                />
                                <div className="flex items-center gap-2">
                                    <Car className="w-5 h-5 text-blue-600" />
                                    <span className="font-medium text-blue-800">
                                        {language === 'el' ? 'Χρειάζεστε όχημα αντικατάστασης;' : 'Need a replacement car?'}
                                    </span>
                                </div>
                            </label>
                        </div>

                        {/* Replacement car form */}
                        {needsReplacementCar && (
                            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                                <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                                    <Car className="w-4 h-4" />
                                    {language === 'el' ? 'Στοιχεία Οχήματος Αντικατάστασης' : 'Replacement Vehicle Details'}
                                </h4>
                                
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="form-group">
                                        <Label className="text-slate-600 text-sm mb-1 block">
                                            {language === 'el' ? 'Μάρκα' : 'Make'} *
                                        </Label>
                                        <Input
                                            value={replacementCarFormData.make}
                                            onChange={(e) => setReplacementCarFormData({...replacementCarFormData, make: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="Toyota"
                                            data-testid="replacement-make-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-slate-600 text-sm mb-1 block">
                                            {language === 'el' ? 'Μοντέλο' : 'Model'}
                                        </Label>
                                        <Input
                                            value={replacementCarFormData.model}
                                            onChange={(e) => setReplacementCarFormData({...replacementCarFormData, model: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="Yaris"
                                            data-testid="replacement-model-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-slate-600 text-sm mb-1 block">
                                            {language === 'el' ? 'Πινακίδα' : 'License Plate'} *
                                        </Label>
                                        <Input
                                            value={replacementCarFormData.license_plate}
                                            onChange={(e) => setReplacementCarFormData({...replacementCarFormData, license_plate: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="ABC-1234"
                                            data-testid="replacement-plate-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="text-slate-600 text-sm mb-1 block">
                                            {language === 'el' ? 'Χιλιόμετρα' : 'Odometer'} *
                                        </Label>
                                        <Input
                                            type="number"
                                            value={replacementCarFormData.odometer_reading}
                                            onChange={(e) => setReplacementCarFormData({...replacementCarFormData, odometer_reading: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder="50000"
                                            data-testid="replacement-odometer-input"
                                        />
                                    </div>
                                    <div className="form-group col-span-2">
                                        <Label className="text-slate-600 text-sm mb-1 block">
                                            {language === 'el' ? 'Επίπεδο Καυσίμου' : 'Fuel Level'}
                                        </Label>
                                        <div className="grid grid-cols-5 gap-2">
                                            {fuelLevelOptions.map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => setReplacementCarFormData({...replacementCarFormData, fuel_level: option.value})}
                                                    className={`p-2 rounded-lg border text-center transition-all ${
                                                        replacementCarFormData.fuel_level === option.value
                                                            ? 'border-blue-500 bg-blue-50'
                                                            : 'border-slate-200 bg-white hover:border-slate-300'
                                                    }`}
                                                    data-testid={`fuel-level-${option.value}`}
                                                >
                                                    <div className={`text-lg ${option.color}`}>{option.icon}</div>
                                                    <div className="text-xs text-slate-600">
                                                        {language === 'el' ? option.labelEl : option.label}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="form-group col-span-2">
                                        <Label className="text-slate-600 text-sm mb-1 block">
                                            {language === 'el' ? 'Σημειώσεις' : 'Notes'}
                                        </Label>
                                        <Input
                                            value={replacementCarFormData.notes}
                                            onChange={(e) => setReplacementCarFormData({...replacementCarFormData, notes: e.target.value})}
                                            className="bg-white border-slate-300 text-slate-900"
                                            placeholder={language === 'el' ? 'Προαιρετικές σημειώσεις...' : 'Optional notes...'}
                                            data-testid="replacement-notes-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex justify-end gap-3 pt-2">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={handleCancelReplacementPrompt}
                                className="border-slate-300 text-slate-600 hover:text-slate-800"
                            >
                                {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                            </Button>
                            <Button 
                                onClick={handleReplacementCarSubmit}
                                className="bg-orange-500 hover:bg-orange-600 text-white"
                                disabled={submittingReplacement}
                                data-testid="confirm-maintenance-btn"
                            >
                                {submittingReplacement ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        <Wrench className="w-4 h-4 mr-2" />
                                        {needsReplacementCar 
                                            ? (language === 'el' ? 'Αποθήκευση & Συνέχεια' : 'Save & Continue')
                                            : (language === 'el' ? 'Αλλαγή σε Συντήρηση' : 'Set to Maintenance')
                                        }
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Vehicle History Modal */}
            <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <History className="w-5 h-5 text-[#1B7EB5]" />
                            Vehicle History - {selectedVehicleHistory?.make} {selectedVehicleHistory?.model}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {historyLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
                        </div>
                    ) : historyData ? (
                        <div className="space-y-6 mt-4">
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-[#1B7EB5]">{historyData.total_inspections}</p>
                                    <p className="text-xs text-zinc-500">Check-ins</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-[#58A6B4]">{historyData.unique_drivers}</p>
                                    <p className="text-xs text-zinc-500">Drivers</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-[#8CC774]">{historyData.period_days}</p>
                                    <p className="text-xs text-zinc-500">Days</p>
                                </div>
                            </div>

                            {/* Driver Usage Summary */}
                            {historyData.driver_usage?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                        <User className="w-4 h-4 text-[#58A6B4]" />
                                        Drivers Who Used This Vehicle
                                    </h4>
                                    <div className="space-y-2">
                                        {historyData.driver_usage.map((driver, index) => (
                                            <div key={driver.driver_id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-[#1B7EB5]/20 rounded-full flex items-center justify-center text-[#1B7EB5] font-bold text-sm">
                                                        {index + 1}
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium">{driver.driver_name}</p>
                                                        <p className="text-xs text-zinc-500">
                                                            {driver.first_use} - {driver.last_use || 'Present'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[#8CC774] font-semibold">{driver.checkin_count}</p>
                                                    <p className="text-xs text-zinc-500">check-ins</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Recent Inspections */}
                            {historyData.inspections?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                        <Camera className="w-4 h-4 text-[#8CC774]" />
                                        Recent Check-ins
                                    </h4>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {historyData.inspections.slice(0, 10).map((inspection) => (
                                            <div key={inspection.id} className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                                                <div className="flex items-center gap-3">
                                                    <Calendar className="w-4 h-4 text-zinc-500" />
                                                    <div>
                                                        <p className="text-white text-sm">{inspection.date}</p>
                                                        <p className="text-xs text-zinc-500">by {inspection.driver_name}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[#1B7EB5] font-medium">{inspection.odometer_reading?.toLocaleString()} km</p>
                                                    {inspection.has_photos && (
                                                        <span className="text-xs text-[#8CC774]">Photos attached</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {historyData.total_inspections === 0 && historyData.total_assignments === 0 && (
                                <div className="text-center py-8 text-zinc-500">
                                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No history found for the last {historyData.period_days} days</p>
                                </div>
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* Replacement Car Return Modal */}
            <Dialog open={returnModalOpen} onOpenChange={setReturnModalOpen}>
                <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <RotateCcw className="w-5 h-5 text-orange-500" />
                            {language === 'el' ? 'Επιστροφή Οχήματος Αντικατάστασης' : 'Return Replacement Vehicle'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {returnLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                        </div>
                    ) : replacementCarData ? (
                        <div className="space-y-6 mt-4">
                            {/* Vehicle Info */}
                            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-orange-500 rounded-lg flex items-center justify-center">
                                        <Truck className="w-7 h-7 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-slate-800">
                                            {replacementCarData.vehicle_info || `${replacementCarData.make} ${replacementCarData.model}`}
                                        </h3>
                                        <p className="text-orange-600 font-mono">{replacementCarData.license_plate}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Received Condition Section */}
                            <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    {language === 'el' ? 'Κατάσταση κατά την Παραλαβή' : 'Condition When Received'}
                                </h4>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Fuel Level */}
                                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Fuel className="w-4 h-4 text-slate-500" />
                                            <span className="text-sm text-slate-600">
                                                {language === 'el' ? 'Στάθμη Καυσίμου' : 'Fuel Level'}
                                            </span>
                                        </div>
                                        <div className={`text-2xl font-mono ${fuelLevelLabels[replacementCarData.fuel_level]?.color || 'text-slate-600'}`}>
                                            {fuelLevelLabels[replacementCarData.fuel_level]?.icon || '----'}
                                        </div>
                                        <p className="text-sm text-slate-500 mt-1">
                                            {fuelLevelLabels[replacementCarData.fuel_level]?.label || replacementCarData.fuel_level}
                                        </p>
                                    </div>

                                    {/* Odometer */}
                                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Truck className="w-4 h-4 text-slate-500" />
                                            <span className="text-sm text-slate-600">
                                                {language === 'el' ? 'Χιλιόμετρα' : 'Odometer'}
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-slate-800">
                                            {replacementCarData.odometer_reading?.toLocaleString()} km
                                        </p>
                                    </div>
                                </div>

                                {/* Notes */}
                                {replacementCarData.notes && (
                                    <div className="mt-4 bg-slate-50 rounded-lg p-4 border border-slate-200">
                                        <p className="text-sm text-slate-600">{replacementCarData.notes}</p>
                                    </div>
                                )}
                            </div>

                            {/* Photos Section */}
                            <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                    <Camera className="w-4 h-4" />
                                    {language === 'el' ? 'Φωτογραφίες κατά την Παραλαβή' : 'Photos When Received'}
                                    <span className="text-xs font-normal text-slate-500">
                                        ({replacementCarData.photos?.length || 0}/6)
                                    </span>
                                </h4>
                                
                                {replacementCarData.photos?.length > 0 ? (
                                    <div className="grid grid-cols-3 gap-3">
                                        {replacementCarData.photos.map((photo, index) => (
                                            <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                                                <img 
                                                    src={`${process.env.REACT_APP_BACKEND_URL}/api/replacement-cars/${replacementCarData.id}/photos/${photo.type}?token=${localStorage.getItem('fleet_token')}`}
                                                    alt={photoTypeLabels[photo.type] || photo.type}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 px-2 text-center">
                                                    {photoTypeLabels[photo.type] || photo.type}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 bg-slate-50 rounded-lg border border-slate-200">
                                        <Image className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                                        <p className="text-sm text-slate-500">
                                            {language === 'el' ? 'Δεν υπάρχουν φωτογραφίες' : 'No photos available'}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Created Info */}
                            <div className="text-xs text-slate-500 border-t border-slate-200 pt-4">
                                {language === 'el' ? 'Δημιουργήθηκε από' : 'Created by'}: {replacementCarData.created_by_name || 'Unknown'} - {new Date(replacementCarData.created_at).toLocaleString()}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setReturnModalOpen(false)}
                                    className="border-slate-300"
                                >
                                    {language === 'el' ? 'Κλείσιμο' : 'Close'}
                                </Button>
                                <Button 
                                    onClick={handleReturnVehicle}
                                    className="bg-orange-500 hover:bg-orange-400 text-white"
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    {language === 'el' ? 'Επιστροφή Οχήματος' : 'Mark as Returned'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-slate-500">
                            <p>{language === 'el' ? 'Δεν βρέθηκαν δεδομένα' : 'No data found'}</p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* AI Maintenance Suggestions Modal */}
            <AiMaintenanceModal
                open={aiMaintenanceModalOpen}
                onOpenChange={setAiMaintenanceModalOpen}
                vehicle={newlyCreatedVehicle}
                language={language}
                onComplete={() => {
                    setNewlyCreatedVehicle(null);
                    fetchData();
                }}
            />

            {/* Vehicle Summary Modal */}
            <VehicleSummaryModal
                isOpen={summaryModalOpen}
                onClose={() => {
                    setSummaryModalOpen(false);
                    setSelectedVehicleSummary(null);
                }}
                vehicle={selectedVehicleSummary}
            />
        </div>
    );
}
